import { currencies } from "@/data/currencies";
import { instrument_types } from "@/data/instrument_types";
import { z } from "zod";

import i18n from "@/i18n";
import {
  dateSchema,
  parseIntValue,
  parseToDateValue,
} from "@/features/FormCard/utils";

import {
  AutocompleteField,
  DatePickerField,
  InputField,
  SelectField,
  TextareaField,
} from "@/features/FormCard/Fields";

import {
  AdjustedData,
  EditForm,
  SubmittedData,
} from "@/features/Edit/types.ts";
import { parseDate } from "@/lib/utils";

export const a7FIAInstrumentsEditForm: EditForm = {
  table_name: "a7_fia_instruments",
  browse_url: "/admin/fia/tree_view?starting_node=instrument",
  fieldsNotApplyTranslation: ["type"],
  adjustData: (submittedData: SubmittedData): AdjustedData => ({
    ...submittedData,
    date_de_maturite: parseDate(submittedData.date_de_maturite),
    date_inactivite: parseDate(submittedData.date_inactivite),
  }),
  formSchema: z.object({
    type: z.string().min(1, "Le type d'instrument type est requis").optional(),
    devise: z.string().min(3, "La devise est requise").optional(),
    code: z.string().nullable().optional(),
    date_de_maturite: z.preprocess(
      (val) => parseToDateValue(val as string),
      dateSchema.nullable(),
    ),
    a7_fia_issuer_id: z.preprocess(
      (val) => parseIntValue(val as string),
      z.number().nullable().optional(),
    ),
    a7_fia_issuer_name: z.string().nullable().optional(),
    nom: z.string().nullable().optional(),
    compagnie: z.string().nullable().optional(),
    contact_fonds: z.string().nullable().optional(),
    contact_afd: z.string().nullable().optional(),
    date_inactivite: z.preprocess(
      (val) => parseToDateValue(val as string),
      dateSchema.nullable().optional(),
    ),
  }),
  formBodyContent: (form, multiFieldOptions) => {
    const instrument_type: string = form.watch("type");

    return (
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <InputField
            name="a7_fia_issuer_id"
            label={i18n.t("a7_fia_instruments.columns.a7_fia_issuer_id", {
              ns: "tables",
              defaultValue: "Issuer ID",
            })}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
          <InputField
            name="a7_fia_issuer_name"
            label={i18n.t("a7_fia_instruments.columns.a7_fia_issuer_name", {
              ns: "tables",
              defaultValue: "Issuer",
            })}
            control={form.control}
            variant="underlined"
            multiFieldOptions={multiFieldOptions}
            fieldOptions={{ isDisabled: true }}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2">
          <SelectField
            name="type"
            label={i18n.t("a7_fia_instruments.columns.type", {
              ns: "tables",
              defaultValue: "Instrument type",
            })}
            isRequired={true}
            options={instrument_types}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
          <AutocompleteField
            name="devise"
            label={i18n.t("a7_fia_instruments.columns.devise", {
              ns: "tables",
              defaultValue: "Currency",
            })}
            isRequired={true}
            options={currencies}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2">
          <InputField
            name="code"
            label={i18n.t("a7_fia_instruments.columns.code", {
              ns: "tables",
              defaultValue: "Code",
            })}
            control={form.control}
          />
          <DatePickerField
            name="date_de_maturite"
            label={i18n.t("a7_fia_instruments.columns.date_de_maturite", {
              ns: "tables",
              defaultValue: "Maturity date",
            })}
            fieldOptions={{
              isVisible: ["LOAN", "BOND", "CONVERTIBLE BOND"].includes(
                instrument_type,
              ),
            }}
            multiFieldOptions={multiFieldOptions}
            control={form.control}
          />
        </div>
        {instrument_type == "FUND" ? (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <InputField
                name="nom"
                label={i18n.t("a7_fia_instruments.columns.nom", {
                  ns: "tables",
                  defaultValue: "Fund name",
                })}
                multiFieldOptions={multiFieldOptions}
                control={form.control}
              />
              <InputField
                name="compagnie"
                label={i18n.t("a7_fia_instruments.columns.compagnie", {
                  ns: "tables",
                  defaultValue: "Company",
                })}
                multiFieldOptions={multiFieldOptions}
                control={form.control}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <TextareaField
                name="contact_fonds"
                label={i18n.t("a7_fia_instruments.columns.contact_fonds", {
                  ns: "tables",
                  defaultValue: "Fund contact",
                })}
                multiFieldOptions={multiFieldOptions}
                control={form.control}
              />
              <TextareaField
                name="contact_afd"
                label={i18n.t("a7_fia_instruments.columns.contact_afd", {
                  ns: "tables",
                  defaultValue: "AFD contact",
                })}
                multiFieldOptions={multiFieldOptions}
                control={form.control}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <DatePickerField
                name="date_inactivite"
                label={i18n.t("a7_fia_instruments.columns.date_inactivite", {
                  ns: "tables",
                  defaultValue: "Inactivity date",
                })}
                multiFieldOptions={multiFieldOptions}
                control={form.control}
              />
            </div>
          </div>
        ) : undefined}
      </div>
    );
  },
};
